import React, { useRef, useState } from "react";
import {
  FabricComponent,
  FabricImport,
  FabricNestResult,
  FabricLayout,
  verifyFabricDxfs,
  calculateFabricNesting,
  fabricErrorMessage,
} from "../../api/fabricNesting";
import "./fabricNesting.css";

const INCH = 25.4;
const MAX_PIECES = 2000;
const colors = [
  "#2563eb",
  "#b45309",
  "#047857",
  "#9333ea",
  "#be123c",
  "#0e7490",
];
type Drawing = FabricImport & {
  key: number;
  quantities: Record<string, string>;
};
type Selected = {
  component: FabricComponent;
  verification: string;
  quantity: number;
};
const inches = (mm: number) => (mm / INCH).toFixed(2);
function path(c: FabricComponent) {
  return c.contours
    .map((ring) => `M${ring.map((p) => p.join(",")).join(" L")} Z`)
    .join(" ");
}
function transform(c: FabricComponent, x: number, y: number, rotation: number) {
  return rotation === 90
    ? `translate(${x + c.height_mm} ${y}) rotate(90)`
    : `translate(${x} ${y})`;
}

export default function FabricNesting() {
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const [units, setUnits] = useState("mm");
  const [height, setHeight] = useState("");
  const [spacing, setSpacing] = useState("0");
  const [margin, setMargin] = useState("0");
  const [rotate, setRotate] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<FabricNestResult | null>(null);
  const [resultItems, setResultItems] = useState<Selected[]>([]);
  const [activeRotation, setActiveRotation] = useState<0 | 90>(0);
  const nextKey = useRef(0);
  const inFlight = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const invalidate = () => {
    setResult(null);
    setMessage("");
  };
  const importsPassed =
    drawings.length > 0 && drawings.every((d) => d.status === "verified");
  const selected: Selected[] = drawings
    .filter((d) => d.status === "verified")
    .flatMap((d) =>
      d.components.map((component) => ({
        component,
        verification: d.verification || "",
        quantity: Number(d.quantities[component.id]),
      }))
    )
    .filter((c) => c.quantity > 0);
  const total = selected.reduce((sum, item) => sum + item.quantity, 0);
  const quantitiesValid = drawings.every(
    (d) =>
      d.status !== "verified" ||
      d.components.every(
        (c) =>
          /^\d+$/.test(d.quantities[c.id]) &&
          Number.isSafeInteger(Number(d.quantities[c.id]))
      )
  );
  const settingsValid =
    height.trim() !== "" &&
    Number.isFinite(Number(height)) &&
    Number(height) > 0 &&
    [spacing, margin].every(
      (v) => v.trim() !== "" && Number.isFinite(Number(v)) && Number(v) >= 0
    ) &&
    Number(margin) * 2 < Number(height);
  const canCalculate =
    importsPassed &&
    confirmed &&
    quantitiesValid &&
    total > 0 &&
    total <= MAX_PIECES &&
    settingsValid;
  const layout = result?.layouts.find((l) => l.rotation === activeRotation);
  const best = result?.layouts.reduce<FabricLayout | undefined>(
    (a, b) => (!a || b.length_mm < a.length_mm ? b : a),
    undefined
  );

  async function upload(files: File[]) {
    if (!files.length || inFlight.current) return;
    invalidate();
    if (
      files.length + drawings.length > 30 ||
      files.some(
        (f) => !/\.dxf$/i.test(f.name) || !f.size || f.size > 10 * 1024 * 1024
      )
    ) {
      setMessage(
        "Import up to 30 non-empty DXF files, each no larger than 10 MB."
      );
      return;
    }
    const names = [
      ...drawings.map((d) => d.filename.toLowerCase()),
      ...files.map((f) => f.name.toLowerCase()),
    ];
    if (new Set(names).size !== names.length) {
      setMessage(
        "Duplicate filenames found. Remove the existing file or rename the new one."
      );
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      const items = await verifyFabricDxfs(files, units);
      setDrawings((prev) => [
        ...prev,
        ...items.map((d) => ({
          ...d,
          key: nextKey.current++,
          quantities: Object.fromEntries(
            (d.components || []).map((c) => [c.id, "1"])
          ),
        })),
      ]);
      setConfirmed(false);
    } catch (e) {
      setMessage(fabricErrorMessage(e));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  async function calculate(e: React.FormEvent) {
    e.preventDefault();
    if (inFlight.current || !canCalculate) return;
    invalidate();
    inFlight.current = true;
    setBusy(true);
    try {
      const data = await calculateFabricNesting(
        {
          fabric_height_mm: Number(height) * INCH,
          spacing_mm: Number(spacing) * INCH,
          edge_margin_mm: Number(margin) * INCH,
          rotation_mode: rotate ? "global_90" : "none",
          items: selected.map((s) => ({
            verification: s.verification,
            component_id: s.component.id,
            quantity: s.quantity,
          })),
        },
        selected.map((s) => s.component)
      );
      setResultItems(selected);
      setResult(data);
      const winner = [...data.layouts].sort(
        (a, b) => a.length_mm - b.length_mm || a.rotation - b.rotation
      )[0];
      if (winner) setActiveRotation(winner.rotation);
    } catch (e) {
      setMessage(fabricErrorMessage(e));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  function download() {
    if (!svgRef.current || !layout) return;
    const svg = svgRef.current.cloneNode(true) as SVGSVGElement;
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svg.setAttribute("width", `${layout.length_mm}mm`);
    svg.setAttribute("height", `${Number(height) * INCH}mm`);
    const blob = new Blob([new XMLSerializer().serializeToString(svg)], {
      type: "image/svg+xml",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fabric-layout-${layout.rotation}deg.svg`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <section
      className="fabric-nesting"
      aria-label="Fabric nesting"
      aria-busy={busy}
    >
      <h2>Fabric nesting</h2>
      <p>
        Import pattern files, choose the quantity of each component, then
        minimise the fabric length used.
      </p>
      <form onSubmit={calculate}>
        <fieldset disabled={busy}>
          <h3>1. Import and check patterns</h3>
          <div className="fabric-nesting__fields">
            <label>
              Units for DXFs without units
              <select
                className="costing-input"
                aria-label="Units for DXFs without units"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
              >
                <option value="mm">Millimetres</option>
                <option value="cm">Centimetres</option>
                <option value="in">Inches</option>
              </select>
            </label>
            <label>
              Add DXF files
              <input
                type="file"
                accept=".dxf"
                multiple
                aria-label="Fabric DXF files"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = "";
                  void upload(files);
                }}
              />
            </label>
          </div>
          <p className="text-muted">
            Units apply to new imports only. Each closed outer pattern becomes a
            component; internal markings must stay with their parent pattern.
            Check dimensions and outlines before continuing.
          </p>
          {drawings.map((d) => (
            <article className="fabric-nesting__drawing" key={d.key}>
              <div className="fabric-nesting__heading">
                <strong>{d.filename}</strong>
                <span>
                  {d.status === "verified"
                    ? `${d.components.length} components · checks passed`
                    : "Import failed"}
                </span>
                <button
                  className="btn btn-sm btn-outline-danger"
                  type="button"
                  aria-label={`Remove ${d.filename}`}
                  onClick={() => {
                    invalidate();
                    setConfirmed(false);
                    setDrawings((prev) => prev.filter((x) => x.key !== d.key));
                  }}
                >
                  Remove file
                </button>
              </div>
              {d.error && <p role="alert">{d.error}</p>}
              {d.warnings.length > 0 && (
                <ul>
                  {d.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              )}
              {d.status === "verified" && (
                <div className="fabric-nesting__components">
                  {d.components.map((c, i) => (
                    <div className="fabric-nesting__component" key={c.id}>
                      <svg
                        viewBox={`-2 -2 ${c.width_mm + 4} ${c.height_mm + 4}`}
                        role="img"
                        aria-label={`${c.name} outline`}
                      >
                        <path
                          d={path(c)}
                          fill={colors[i % colors.length]}
                          fillOpacity="0.15"
                          fillRule="evenodd"
                          stroke={colors[i % colors.length]}
                          strokeWidth="1"
                          vectorEffect="non-scaling-stroke"
                        />
                      </svg>
                      <strong>{c.name}</strong>
                      <span>
                        {inches(c.width_mm)} × {inches(c.height_mm)} in
                      </span>
                      <label>
                        Quantity
                        <input
                          className="costing-input"
                          type="number"
                          min="0"
                          max={MAX_PIECES}
                          step="1"
                          required
                          aria-label={`${d.filename} ${c.name} quantity`}
                          value={d.quantities[c.id]}
                          onChange={(e) => {
                            invalidate();
                            const value = e.target.value;
                            setDrawings((prev) =>
                              prev.map((row) =>
                                row.key === d.key
                                  ? {
                                      ...row,
                                      quantities: {
                                        ...row.quantities,
                                        [c.id]: value,
                                      },
                                    }
                                  : row
                              )
                            );
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
          {drawings.some((d) => d.status === "error") && (
            <p role="alert">
              Remove or fix failed files before entering fabric requirements.
            </p>
          )}
          {importsPassed && (
            <>
              <h3>2. Fabric requirements</h3>
              <label className="fabric-nesting__check">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => {
                    invalidate();
                    setConfirmed(e.target.checked);
                  }}
                />
                I checked the extracted outlines, dimensions and their common
                grain direction. The patterns already include the required seam
                allowance.
              </label>
              <div className="fabric-nesting__fields">
                {(
                  [
                    ["Fabric height (inches)", height, setHeight, "0.001"],
                    ["Gap between pieces (inches)", spacing, setSpacing, "0"],
                    ["Edge margin (inches)", margin, setMargin, "0"],
                  ] as const
                ).map(([label, value, setter, min]) => (
                  <label key={label}>
                    {label}
                    <input
                      className="costing-input"
                      aria-label={label}
                      type="number"
                      required
                      min={min}
                      step="any"
                      value={value}
                      onChange={(e) => {
                        invalidate();
                        setter(e.target.value);
                      }}
                    />
                  </label>
                ))}
              </div>
              <p>
                Height is the fixed roll width, shown vertically. Length extends
                horizontally and is calculated automatically. Margins apply on
                all four sides. Set a component’s quantity to 0 to exclude it.
              </p>
              <label className="fabric-nesting__check">
                <input
                  type="checkbox"
                  checked={rotate}
                  onChange={(e) => {
                    invalidate();
                    setRotate(e.target.checked);
                  }}
                />
                Allow rotation of the entire set by 90°
              </label>
              <p className="text-muted">
                Compare all pieces as imported against all pieces turned 90°.
                Individual rotations, 180° flips and mirroring are never
                allowed. Align all patterns to a common grain direction in the
                source DXFs.
              </p>
              <p>
                {total.toLocaleString()} pieces selected · maximum{" "}
                {MAX_PIECES.toLocaleString()}
              </p>
              {(!quantitiesValid || total > MAX_PIECES) && (
                <p role="alert">
                  Enter whole quantities from 0, with no more than 2,000 pieces
                  in total.
                </p>
              )}
              <button
                className="btn btn-primary"
                type="submit"
                disabled={!canCalculate}
              >
                Find shortest layout
              </button>
            </>
          )}
        </fieldset>
      </form>
      {busy && (
        <p role="status">
          Processing patterns… This may take up to two minutes.
        </p>
      )}
      {message && (
        <p className="alert alert-danger mt-3" role="alert">
          {message}
        </p>
      )}
      {result && (
        <div className="fabric-nesting__result">
          <h3>3. Cutting layout</h3>
          <p>
            Best layout found by the optimiser; a mathematical minimum is not
            guaranteed.
            {result.time_limited ? " The search reached its time limit." : ""}
          </p>
          <div className="fabric-nesting__fields">
            {result.layouts.map((l) => (
              <button
                key={l.rotation}
                type="button"
                className={`btn ${
                  activeRotation === l.rotation
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                aria-pressed={activeRotation === l.rotation}
                onClick={() => setActiveRotation(l.rotation)}
              >
                {l.rotation === 0 ? "All as imported" : "All rotated 90°"} ·{" "}
                {inches(l.length_mm)} in{best === l ? " · shortest found" : ""}
              </button>
            ))}
          </div>
          {result.infeasible_orientations.map((o) => (
            <p key={o.rotation}>
              {o.rotation}° layout cannot fit: {o.reason}
            </p>
          ))}
          {layout && (
            <>
              <p>
                <strong>
                  Length: {inches(layout.length_mm)} in (
                  {(layout.length_mm / 1000).toFixed(3)} m)
                </strong>{" "}
                · Height: {height} in · Fabric utilisation:{" "}
                {layout.utilization_percent.toFixed(1)}% ·{" "}
                {layout.placements.length} pieces
              </p>
              <div className="fabric-nesting__plot">
                <svg
                  ref={svgRef}
                  role="img"
                  aria-label="Fabric cutting layout"
                  viewBox={`0 0 ${layout.length_mm} ${Number(height) * INCH}`}
                >
                  <title>
                    Fabric layout: {inches(layout.length_mm)} inches long,{" "}
                    {height} inches high. All pieces at {layout.rotation}{" "}
                    degrees.
                  </title>
                  <rect
                    x="0"
                    y="0"
                    width={layout.length_mm}
                    height={Number(height) * INCH}
                    fill="white"
                    stroke="#64748b"
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                  />
                  {layout.placements.map((p, i) => {
                    const c = resultItems[p.item_index].component;
                    return (
                      <path
                        key={i}
                        d={path(c)}
                        transform={transform(c, p.x_mm, p.y_mm, p.rotation)}
                        fill={colors[p.item_index % colors.length]}
                        fillOpacity="0.18"
                        fillRule="evenodd"
                        stroke={colors[p.item_index % colors.length]}
                        strokeWidth="1"
                        vectorEffect="non-scaling-stroke"
                      >
                        <title>
                          {c.name} · piece {i + 1} · {p.rotation}°
                        </title>
                      </path>
                    );
                  })}
                </svg>
              </div>
              <div className="fabric-nesting__legend">
                {resultItems.map((s, i) => (
                  <span key={i}>
                    <i style={{ background: colors[i % colors.length] }} />
                    {s.component.name} × {s.quantity}
                  </span>
                ))}
              </div>
              <button
                className="btn btn-outline-primary"
                type="button"
                onClick={download}
              >
                Download layout SVG
              </button>
              <p className="text-muted">
                SVG dimensions are in millimetres at 1:1 scale. Verify a known
                dimension in your plotting software before cutting.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
